import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

const loader = new GLTFLoader();

export function loadGLB(path: string): Promise<GLTF> {
  return new Promise((resolve, reject) => {
    loader.load(
      path,
      (gltf) => resolve(gltf),
      undefined,
      (err) => reject(err instanceof Error ? err : new Error(String(err))),
    );
  });
}

/**
 * Find the first material in the scene whose name matches `materialName`.
 * Returns both the material and the mesh it belongs to (useful for export).
 */
export function findMaterialByName(
  root: THREE.Object3D,
  materialName: string,
): { mesh: THREE.Mesh; material: THREE.MeshStandardMaterial } | null {
  let found:
    | { mesh: THREE.Mesh; material: THREE.MeshStandardMaterial }
    | null = null;

  root.traverse((obj) => {
    if (found) return;
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;

    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    for (const m of materials) {
      if (m && m.name === materialName) {
        found = { mesh, material: m as THREE.MeshStandardMaterial };
        return;
      }
    }
  });

  return found;
}

/**
 * Apply CanvasTextures for the base color map AND the emissive map to the
 * material named `materialName`. Returns both textures so the caller can
 * flag them with `needsUpdate = true` whenever either canvas changes.
 *
 * The emissive colour is set to white with intensity = 2 so that the
 * canvas's pixel brightness (baked per-layer as `intensity/2`) maps
 * faithfully to a final emission value in the 0..2 range.
 */
export function applyCanvasToMaterial(
  root: THREE.Object3D,
  materialName: string,
  baseCanvas: HTMLCanvasElement,
  emissiveCanvas: HTMLCanvasElement,
): {
  texture: THREE.CanvasTexture;
  emissiveTexture: THREE.CanvasTexture;
  material: THREE.MeshStandardMaterial;
} | null {
  const hit = findMaterialByName(root, materialName);
  if (!hit) return null;

  const texture = new THREE.CanvasTexture(baseCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = false; // GLTF convention.
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 8;
  texture.needsUpdate = true;

  const emissiveTexture = new THREE.CanvasTexture(emissiveCanvas);
  emissiveTexture.colorSpace = THREE.SRGBColorSpace;
  emissiveTexture.flipY = false;
  emissiveTexture.wrapS = THREE.ClampToEdgeWrapping;
  emissiveTexture.wrapT = THREE.ClampToEdgeWrapping;
  emissiveTexture.anisotropy = 8;
  emissiveTexture.needsUpdate = true;

  // Dispose any previous textures on the material.
  if (hit.material.map) hit.material.map.dispose();
  if (hit.material.emissiveMap) hit.material.emissiveMap.dispose();

  hit.material.map = texture;
  hit.material.emissiveMap = emissiveTexture;
  hit.material.emissive = new THREE.Color(1, 1, 1);
  hit.material.emissiveIntensity = 2;
  hit.material.needsUpdate = true;

  return { texture, emissiveTexture, material: hit.material };
}
